import { Component, OnDestroy, OnInit } from '@angular/core';
import { StorageService, StorageType, TranslationService } from 'gast-common-ng';
import { BsLocaleService } from 'ngx-bootstrap';
import { NgxSpinnerService } from 'ngx-spinner';
import { Subscription } from 'rxjs';
import { VerificationDmatFormDto } from '../shared/dto/verification-dmat-form.dto';
import { CertificateService } from '../shared/services/certificate.service';
import { DateStringUtil } from '../shared/utils/date-string-util';
import { ActivatedRoute } from '@angular/router';
import { TestasDigitalResultDto } from '../shared/dto/testas-digital-result.dto';
import { DateUtil } from '../shared/utils/date-util';
import { LanguageUtil } from '../shared/utils/language-util';
import { DmatModuleDto } from '../shared/dto/dmat-module.dto';

@Component({
  selector: 'app-verification-dmat-form',
  templateUrl: './verification-dmat-form.component.html',
  styleUrls: ['./verification-dmat-form.component.css']
})
export class VerificationDmatFormComponent implements OnInit, OnDestroy {
  result: boolean;
  isSubmitted = false;
  hasError = false;
  hasTechnicalError = false;
  isLoading = false;

  // inputs
  participantNumber: string;
  participantName: string;
  dateOfBirth: Date;
  examDate: Date;
  kernPr: string;
  kernScore: string;
  fachPr: string;
  fachScore: string;
  fachmodul: string;

  modules: Array<DmatModuleDto> = [];
  modulesLoadFailed = false;

  subscription = new Subscription();

  token: string;
  hash: string;

  constructor(
    private localeService: BsLocaleService,
    private certificationService: CertificateService,
    private spinnerService: NgxSpinnerService,
    private storageService: StorageService,
    private route: ActivatedRoute,
    private translationService: TranslationService) {
  }

  ngOnInit() {
    // init datepicker locale
    this.localeService.use('de');

    this.subscription = this.route.queryParams.subscribe(params => {
      this.token = params.token;
      this.hash = params.hash;
      if (this.token && this.hash) {
        this.certificationService.getDmatDigitalResults(this.token, this.hash);
        this.spinnerService.show();
      }
    });

    // subscribe to digital result verification
    this.subscription.add(this.certificationService.dmatDigitalQrResultLoaded.subscribe((result: TestasDigitalResultDto) => {
      this.spinnerService.hide();
      if (result) {
        const langCode = LanguageUtil.toLangCode(result.language);
        if (langCode) {
          this.translationService.changeLanguageUsingCode(langCode);
        }
        this.participantNumber = result.teilnehmerNr;
        this.participantName = result.fullname;
        this.dateOfBirth = DateUtil.formatStringToDate(result.birthday);
        this.examDate = DateUtil.formatStringToDate(result.examDate);
        this.kernPr = result.kernPr;
        this.kernScore = result.kernScore;
        this.fachPr = result.fachPr;
        this.fachScore = result.fachScore;
        this.fachmodul = result.fachmodul;
        this.submit();
      }
      this.isLoading = false;
    }));

    this.subscription.add(this.certificationService.dmatModulesLoaded.subscribe((modules: Array<DmatModuleDto>) => {
      this.modules = modules;
      this.sortModules();
      this.modulesLoadFailed = false;
    }));
    this.subscription.add(this.translationService.onLanguageChanged().subscribe(() => this.sortModules()));
    this.subscription.add(this.certificationService.dmatModulesError.subscribe(() => {
      this.modulesLoadFailed = true;
    }));
    this.certificationService.getDmatModules();

    this.subscription.add(this.certificationService.dmatDigitalResultLoaded.subscribe((result: boolean) => {
      this.spinnerService.hide();
      this.result = result;
      this.isLoading = false;
    }));

    this.subscription.add(this.certificationService.verificationError.subscribe(() => {
      this.spinnerService.hide();
      this.result = false;
      this.isSubmitted = true;
      this.hasTechnicalError = true;
      this.isLoading = false;
    }));

    // restore inputs
    this.restoreInputs();
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  /** set config for date picker */
  getBsConfig() {
    return { containerClass: 'theme-orange' };
  }

  getModuleName(module: DmatModuleDto): string {
    return this.translationService.getCurrentLanguageCode() === 'en' ? module.nameEn : module.nameDe;
  }

  /** sort modules alphabetically by their name in the current language */
  private sortModules(): void {
    const languageCode = this.translationService.getCurrentLanguageCode();
    this.modules = [...this.modules].sort((a, b) =>
      (this.getModuleName(a) || '').localeCompare(this.getModuleName(b) || '', languageCode));
  }

  submit(): void {
    this.isSubmitted = true;
    this.logDmat();
    if (this.participantNumber && this.participantName && this.dateOfBirth && this.examDate) {
      const dto = new VerificationDmatFormDto();
      dto.participantNumber = this.participantNumber;
      dto.participantName = this.participantName;
      dto.dateOfBirth = DateStringUtil.getDateString(this.dateOfBirth);
      dto.examDate = DateStringUtil.getDateString(this.examDate);
      dto.kernPr = this.kernPr;
      dto.kernScore = this.kernScore;
      dto.fachPr = this.fachPr;
      dto.fachScore = this.fachScore;
      dto.fachmodul = this.fachmodul;
      this.certificationService.getDmatDigitalFormResults(dto);
      this.storageService.setItem('VERIFICATION_FORM', dto, StorageType.Local);
      this.spinnerService.show();
      this.isLoading = true;
      this.hasError = false;
      this.hasTechnicalError = false;
    } else {
      this.hasError = true;
    }
  }

  logDmat(): void {
    console.log('participant number: ' + this.participantNumber);
    console.log('participant name: ' + this.participantName);
    console.log('date of birth: ' + DateStringUtil.getDateString(this.dateOfBirth));
    console.log('exam date: ' + DateStringUtil.getDateString(this.examDate));
    console.log('kernPr: ' + this.kernPr);
    console.log('kernScore: ' + this.kernScore);
    console.log('fachPr: ' + this.fachPr);
    console.log('fachScore: ' + this.fachScore);
    console.log('fachmodule: ' + this.fachmodul);
  }

  private restoreInputs() {
    const dto = this.storageService.getItem('VERIFICATION_FORM', StorageType.Local);
    if (dto) {
      this.participantNumber = dto.participantNumber;
      this.participantName = dto.participantName;
      this.dateOfBirth = new Date(dto.dateOfBirth);
      this.examDate = new Date(dto.examDate);
      this.kernPr = dto.kernPr;
      this.kernScore = dto.kernScore;
      this.fachPr = dto.fachPr;
      this.fachScore = dto.fachScore;
      this.fachmodul = dto.fachmodul;
    }
  }
}
